import apiClient from './api-client';

export interface Language {
    code: string;
    name: string;
    localeName: string;
    translationProvider: string | null;
}

export const languageService = {
    /**
     * Get all supported languages from the portal API
     * Always ensures English is included in the list (even if not returned by API)
     */
    getSupportedLanguages: async (): Promise<Language[]> => {
        try {
            const response = await apiClient.get<{
                success: boolean;
                data: Language[];
                error?: string;
            }>('/portal/languages');
            if (!response.success) {
                throw new Error(response.error || 'Failed to fetch supported languages');
            }

            const languages = response.data;
            const englishLanguage = languageService.getDefaultLanguage();

            // Ensure English is always included
            const hasEnglish = languages.some(lang => lang.code === 'en');
            if (!hasEnglish) {
                // Add English at the beginning of the list
                return [englishLanguage, ...languages];
            }

            return languages;
        } catch (error: any) {
            console.error('Error fetching supported languages:', error);
            // On error, at least return English
            return [languageService.getDefaultLanguage()];
        }
    },

    /**
     * Get default language (English)
     */
    getDefaultLanguage: (): Language => {
        return { code: 'en', name: 'English', localeName: 'English', translationProvider: null };
    },

    /**
     * Check if a language code is valid
     */
    isValidLanguageCode: async (code: string): Promise<boolean> => {
        try {
            const languages = await languageService.getSupportedLanguages();
            return languages.some(lang => lang.code === code);
        } catch (error) {
            console.error('Error validating language code:', error);
            return false;
        }
    },

    /**
     * Get language by code
     */
    getLanguageByCode: async (code: string): Promise<Language | undefined> => {
        try {
            const languages = await languageService.getSupportedLanguages();
            return languages.find(lang => lang.code === code);
        } catch (error) {
            console.error('Error getting language by code:', error);
            return undefined;
        }
    }
};

